import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  CloseCircleOutlineIcon,
  CloseIcon,
  ExclamationIcon,
  MagnifyIcon,
  ViewColumnIcon,
  ViewListIcon,
} from '@atgfr8/icons';
import { Button, SelectInput, TabsComponent, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { BasicModal } from '@components/Modal/BasicModal';
import { USER_FEATURE_NAMES } from '@constants/featureFlags';
import { useNavigate } from '@solidjs/router';
import { dateComparator } from '@store/loadboard/utils';
import { getEffectiveIsEnabledForUserByFeatureName } from '@store/Global/service';
import { closeModal, openModal } from '@store/modals';
import {
  getCustomerLookup,
  getProspectDuplicateCandidates,
  getProspectsGridData,
} from '@store/prospect/services';
import type {
  ProspectDuplicateCandidate,
  ProspectGridData,
} from '@store/prospect/types';
import { ConfigManager } from '@utils/ConfigManager';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { routeCustomerDetails } from '@utils/routeCustomerDetails';
import { routeProspectDetails } from '@utils/routeProspectDetails';
import { handleToast } from '@utils/utils';
import { CustomerHeader } from '@views/customer/CustomerHeader';
import { atgLogo } from '@views/ltl';
import { IMPORT_PROSPECT_MODAL_ID } from '@views/order/constants';
import { DoNotSolicitCustomers } from '@views/revenueDashboard/overview/components/DoNotSolicitCustomers';
import uniq from 'lodash/uniq';
import { DateTime } from 'luxon';
import { Component, createSignal, For, onMount, Show } from 'solid-js';

import cls from './classes';
import { ImportProspectModal } from './ImportProspectModal';
import ListView from './ListView';

export type ProspectSortType = {
  field: string;
  order: 'asc' | 'desc';
  followUpStartDate: DateTime | null;
  followUpEndDate: DateTime | null;
};

export type ProspectMatching = {
  prospectName: string;
  prospectCity: string;
  prospectState: string;
  candidates: ProspectDuplicateCandidate[];
  prospectMatches: {
    name?: string;
    status?: string;
    subStatus?: string | null;
  }[];
  prospectsSearched: boolean;
  tooManyMatches: boolean;
  isLoading: boolean;
};

const PROSPECT_DUPLICATE_CANDIDATES_MODAL_ID = 'prospect-duplicate-candidates';

const ProspectPage: Component = () => {
  const [sortBy, setSortBy] = createSignal<ProspectSortType>({
    field: 'Name',
    order: 'asc',
    followUpStartDate: null,
    followUpEndDate: null,
  });
  const [activeView, setActiveView] = createSignal('list');
  const [activeFollowUpTab, setActiveFollowUpTab] = createSignal(0);
  const [filters, setFilters] = createSignal({
    accountRep: '',
    prospectName: '',
  });
  const [propspectMatching, setProspectMatching] =
    createSignal<ProspectMatching>({
      prospectName: '',
      prospectCity: '',
      prospectState: '',
      candidates: [],
      prospectMatches: [],
      prospectsSearched: false,
      tooManyMatches: false,
      isLoading: false,
    });
  const [isProspectFuzzyApiEnabled, setIsProspectFuzzyApiEnabled] =
    createSignal(false);
  const [refetch, setRefetch] = createSignal(false); // Used to refetch data after import for list view
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);

  const navigate = useNavigate();

  onMount(async () => {
    const isEnabled =
      (await getEffectiveIsEnabledForUserByFeatureName(
        USER_FEATURE_NAMES.prospectFuzzyApi,
      )) ?? false;

    setIsProspectFuzzyApiEnabled(isEnabled);
  });

  const commonFilterParams = {
    suppressAndOrCondition: true,
    filterOptions: ['startsWith'],
    buttons: ['apply', 'reset'],
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 600,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Lead', 'Quoting', 'Contacted', 'Submitted'],
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
      },
      minWidth: 120,
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: commonFilterParams,
      minWidth: 160,
    },
    {
      headerName: 'Last Contact Date',
      field: 'lastContactDate',
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params: { value: string }) => {
        return params.value
          ? DateTime.fromISO(params.value.replace('Z', '')).toFormat(
              'EEE, MMM dd, yyyy h:mm a',
            )
          : '';
      },
      filterParams: {
        comparator: dateComparator,
        buttons: ['apply', 'reset'],
        suppressAndOrCondition: true,
        filterOptions: ['lessThan', 'greaterThan', 'inRange', 'equals'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      minWidth: 185,
    },
    {
      headerName: 'Follow Up Date',
      field: 'followUpDate',
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params: { value: string }) => {
        return params.value
          ? DateTime.fromISO(params.value.replace('Z', '')).toFormat(
              'EEE, MMM dd, yyyy h:mm a',
            )
          : '';
      },
      filterParams: {
        comparator: dateComparator,
        buttons: ['apply', 'reset'],
        suppressAndOrCondition: true,
        filterOptions: ['lessThan', 'greaterThan', 'inRange', 'equals'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      minWidth: 185,
    },
  ];

  const handleListView = () => {
    setActiveView((prev) => (prev === 'list' ? 'table' : 'list'));
  };

  const filterTypeMapping = {
    equals: 'EQUALS',
    notEqual: 'NOT_EQUALS',
    greaterThan: 'GREATER_THAN',
    greaterThanOrEqual: 'GREATER_THAN_OR_EQUALS',
    lessThan: 'LESS_THAN',
    lessThanOrEqual: 'LESS_THAN_OR_EQUALS',
    contains: 'CONTAINS',
    notContains: 'NOT_CONTAINS',
    startsWith: 'STARTS_WITH',
    endsWith: 'ENDS_WITH',
    blank: 'BLANK',
    notBlank: 'NOT_BLANK',
    inRange: 'IN_RANGE',
    set: 'EQUALS',
  };

  const transformFilters = (inputFilters: { [key: string]: unknown }) => {
    const outputFilters = Object.keys(inputFilters).map((key) => {
      const filter = inputFilters[key] as {
        filterType: string;
        type: keyof typeof filterTypeMapping;
        filter: string;
        dateFrom?: string | null;
        dateTo?: string | null;
        values?: string[];
      };
      if ((filter as { filterType: string }).filterType === 'date') {
        const dateFilters = [];

        if (Boolean(filter.dateFrom)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateFrom as string,
              filter.dateFrom?.length === 19
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }

        if (Boolean(filter.dateTo)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateTo as string,
              filter.dateFrom?.length === 19
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }
        return {
          field: key,
          filterType:
            filterTypeMapping[
              (filter as { type: keyof typeof filterTypeMapping }).type
            ],
          values: dateFilters,
        };
      }
      if ((filter as { filterType: string }).filterType === 'set') {
        return (
          (filter.values ?? []).length > 0 && {
            field: key,
            filterType: 'IN',
            values: filter.values,
          }
        );
      }
      if ((filter as { filterType: string }).filterType === 'text') {
        return {
          field: key,
          filterType:
            filterTypeMapping[
              (filter as { type: keyof typeof filterTypeMapping }).type
            ],
          values: [(filter as { filter: string }).filter],
        };
      }
    });

    return outputFilters.filter(Boolean);
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: ProspectGridData[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: [
            {
              field: 'status',
              filterType: 'NOT_EQUALS',
              values: ['approved'],
            },
            {
              field: 'status',
              filterType: 'NOT_EQUALS',
              values: ['dead'],
            },
            {
              field: 'status',
              filterType: 'NOT_EQUALS',
              values: ['Dead Lead'],
            },
            ...transformFilters(params.request.filterModel),
          ],
          sort: {
            field: orderBy || 'createdDate',
            direction: sortBy || 'asc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 1000,
        };

        try {
          gridRef()?.api.showLoadingOverlay();
          const data = await getProspectsGridData(filteredObject);
          params.success({
            rowData: data?.value.data ?? [],
            rowCount: data?.value.totalCount ?? 0,
          });
        } catch (error) {
          params.fail();
        } finally {
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  const followUpTabs = [
    'Any',
    'Today',
    'Tomorrow',
    'This Week',
    'Next Week',
    'This Month',
    'Next Month',
  ];

  const onListViewTabClick = (index: number) => {
    setActiveFollowUpTab(index);

    const now = DateTime.now();
    let startDate: DateTime | null = null;
    let endDate: DateTime | null = null;

    switch (index) {
      case 1:
        startDate = now.startOf('day');
        endDate = now.endOf('day');
        break;
      case 2:
        startDate = now.plus({ days: 1 }).startOf('day');
        endDate = now.plus({ days: 1 }).endOf('day');
        break;
      case 3:
        startDate = now.startOf('week');
        endDate = now.endOf('week');
        break;
      case 4:
        startDate = now.plus({ weeks: 1 }).startOf('week');
        endDate = now.plus({ weeks: 1 }).endOf('week');
        break;
      case 5:
        startDate = now.startOf('month');
        endDate = now.endOf('month');
        break;
      case 6:
        const nextMonth = now.plus({ months: 1 });
        startDate = nextMonth.startOf('month');
        endDate = nextMonth.endOf('month');
        break;
      default:
        break;
    }

    setSortBy((prev) => ({
      ...prev,
      field: 'Follow Up Date',
      order: 'desc',
      followUpStartDate: startDate,
      followUpEndDate: endDate,
    }));
  };

  const isExactMatch = () => {
    return (
      propspectMatching().prospectMatches.length === 1 &&
      propspectMatching().prospectMatches[0]?.name?.toLowerCase() ===
        propspectMatching().prospectName.toLowerCase()
    );
  };

  const isPartialMatch = () => {
    return propspectMatching().prospectMatches.some((match) =>
      match.name
        ?.toLowerCase()
        .includes(propspectMatching().prospectName.toLowerCase()),
    );
  };

  const noMatch = () => {
    return (
      propspectMatching().prospectsSearched &&
      propspectMatching().prospectMatches.length === 0
    );
  };

  const activeCustomerLookUpSearch = async () => {
    const { prospectName, prospectState } = propspectMatching();
    const name = prospectName.trim();
    if (!name) {
      handleToast(ToastType.Error, 'Please enter a valid customer name');
      return;
    }

    setProspectMatching((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const res = await getCustomerLookup({
        name,
        state: prospectState.trim() || undefined,
      });

      if (res && res.isSuccess) {
        const uniqueMatches = uniq(res.value);
        const tooManyMatches = uniqueMatches.length >= 25;

        setProspectMatching((prev) => ({
          ...prev,
          prospectsSearched: true,
          prospectMatches: uniqueMatches,
          tooManyMatches,
        }));

        if (tooManyMatches) {
          handleToast(
            ToastType.Caution,
            'Your search returned too many potential matches, please reach out to the Credit team for further assistance',
          );
        }
      }
    } finally {
      setProspectMatching((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const activeCustomerLookUpSearchIcon = () => {
    if (noMatch()) {
      return <CheckIcon class="text-[#3c763d]" />;
    }
    if (isExactMatch()) {
      return <CloseIcon class="text-red-500" />;
    }
    if (isPartialMatch()) {
      return <ExclamationIcon />;
    }
    return null;
  };

  const activeCustomerLookupControlStyle = () => ({
    'padding-right': '8px',
    ...(!isProspectFuzzyApiEnabled() &&
    (noMatch() || isExactMatch() || isPartialMatch())
      ? { 'border-color': '#3c763d' }
      : {}),
    'background-color':
      !isProspectFuzzyApiEnabled() && noMatch()
        ? '#87F99A'
        : !isProspectFuzzyApiEnabled() && isExactMatch()
          ? '#f2dede'
          : !isProspectFuzzyApiEnabled() && isPartialMatch()
            ? '#fcf8e3'
            : '',
  });

  const reviewDuplicateCandidates = async () => {
    const { prospectName, prospectCity, prospectState } = propspectMatching();
    const name = prospectName.trim();
    const city = prospectCity.trim();
    const state = prospectState.trim();
    if (name.length < 3 || (name.match(/[a-z]/gi)?.length ?? 0) < 2) {
      handleToast(
        ToastType.Error,
        'Enter a company name with at least three characters and two letters.',
      );
      return;
    }

    setProspectMatching((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const res = await getProspectDuplicateCandidates({
        companyName: name,
        city: city || undefined,
        state: state || undefined,
        allowNameOnly: !city || !state,
      });

      if (!res.isSuccess) {
        handleToast(
          ToastType.Error,
          res.errors[0]?.message ?? 'Potential match review failed.',
        );
        return;
      }

      const candidates: ProspectDuplicateCandidate[] = res.value.candidates;
      if (candidates.length === 0) {
        handleToast(ToastType.Info, 'No potential matches found.');
        return;
      }

      setProspectMatching((prev) => ({
        ...prev,
        candidates,
      }));
      openModal(PROSPECT_DUPLICATE_CANDIDATES_MODAL_ID);
    } catch {
      handleToast(
        ToastType.Error,
        'Potential match review is unavailable. Please try again.',
      );
    } finally {
      setProspectMatching((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const reviewPotentialMatches = async () => {
    if (isProspectFuzzyApiEnabled()) {
      await reviewDuplicateCandidates();
      return;
    }

    await activeCustomerLookUpSearch();
  };

  const openCandidate = (candidate: ProspectDuplicateCandidate) => {
    closeModal(PROSPECT_DUPLICATE_CANDIDATES_MODAL_ID);
    if (candidate.recordType === 'Customer') {
      if (routeCustomerDetails(candidate.sourceId)) {
        navigate(`/Customer/Details/${candidate.sourceId}`);
      }
      return;
    }

    if (routeProspectDetails(String(candidate.sourceId))) {
      navigate(`/Prospect/Details/${candidate.sourceId}`);
    }
  };

  return (
    <>
      {ConfigManager.showLayout && <CustomerHeader title={'Prospects'} />}
      <Stack p={1.5} class={cls.prospectPage}>
        <Show when={propspectMatching().isLoading || refetch()}>
          <AtgLoader />
        </Show>
        <Box p={1} class="px-0">
          <Grid container spacing={1}>
            <Grid item size={{ xs: 12, md: 8 }} backgroundColor="#F5F5F5" p={1}>
              <Grid container spacing={1}>
                <Grid item size={{ xs: 12, md: 1.2 }}>
                  <Typography variant="body2" class={cls.changeViewText}>
                    Change View
                  </Typography>
                  <Box class="flex flex-wrap" alignItems="center" gap={1}>
                    <IconButton
                      title={'View List'}
                      class={`
                      ${activeView() === 'list' && cls.activeView}`}
                      style={{
                        'border-radius': '2px',
                        border: '1px solid #1B4960',
                        padding: '5px',
                      }}
                      onClick={handleListView}
                      dataTestId="prospect-view-list-button"
                    >
                      <ViewColumnIcon class={cls.changeViewIcon} />
                    </IconButton>
                    <IconButton
                      title={'View Table'}
                      class={`
                      ${activeView() === 'table' && cls.activeView}`}
                      style={{
                        'border-radius': '2px',
                        border: '1px solid #1B4960',
                        padding: '5px',
                      }}
                      onClick={() =>
                        setActiveView((prev) =>
                          prev === 'table' ? 'list' : 'table',
                        )
                      }
                      dataTestId="prospect-view-table-button"
                    >
                      <ViewListIcon class={cls.changeViewIcon} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 10.8 }}>
                  <Stack spacing={1}>
                    <Box gap={0.8} class="flex flex-wrap">
                      <TextField
                        class="w-fit! min-w-[180px]!"
                        label="Account Rep"
                        placeholder="Enter a person"
                        value={filters().accountRep}
                        onChange={(v: string) =>
                          setFilters((prev) => ({ ...prev, accountRep: v }))
                        }
                        dataTestId={'account-rep-textInput'}
                      />
                      <TextField
                        class="w-fit! min-w-[250px]!"
                        label="Prospect Name"
                        placeholder="Search My Prospects"
                        value={filters().prospectName}
                        onChange={(v: string) =>
                          setFilters((prev) => ({ ...prev, prospectName: v }))
                        }
                        dataTestId={'prospect-name-textInput'}
                      />
                      <Box class="flex items-center gap-1 shrink-0">
                        <Box class="w-37.5 min-w-37.5">
                          <SelectInput
                            label="Sorting"
                            menuItems={[
                              'Name',
                              'Last Contact Date',
                              'Follow Up Date',
                              'Created Date',
                            ].map((s) => ({ label: s, value: s }))}
                            value={sortBy().field}
                            onChange={(val) =>
                              setSortBy((prev) => ({
                                ...prev,
                                field: val,
                              }))
                            }
                            dataTestId={formatDataTestId(
                              'Sorting',
                              'selectInput',
                            )}
                          />
                        </Box>
                        <IconButton
                          title={
                            sortBy().order === 'asc'
                              ? 'Sort Descending'
                              : 'Sort Ascending'
                          }
                          class={cls.sortingIconButton}
                          style={{
                            'border-radius': '4px',
                          }}
                          onClick={() =>
                            setSortBy((prev) => ({
                              ...prev,
                              order: prev.order === 'asc' ? 'desc' : 'asc',
                            }))
                          }
                          dataTestId="prospect-sort-order-button"
                        >
                          <Show
                            when={sortBy().order === 'asc'}
                            fallback={<ArrowDownIcon />}
                          >
                            <ArrowUpIcon />
                          </Show>
                        </IconButton>
                      </Box>
                      <Button
                        disabled={refetch() || activeView() === 'table'}
                        class={cls.searchBtn}
                        label="Search"
                        startIcon={<MagnifyIcon size={20} />}
                        variant="contained"
                        type="button"
                        onClick={() => {
                          setRefetch(true);
                        }}
                        dataTestId={formatDataTestId('Search', 'button')}
                      />
                      <Button
                        class={cls.searchBtn}
                        label="Import"
                        startIcon={
                          <img
                            src={ExcelSheetIcon}
                            height="18px"
                            alt="Excel Sheet"
                          />
                        }
                        variant="contained"
                        type="button"
                        onClick={() => openModal(IMPORT_PROSPECT_MODAL_ID)}
                        dataTestId={formatDataTestId('Import', 'button')}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      class={cls.activeCustomerLookUpText}
                    >
                      Active Customer Lookup
                    </Typography>
                    <Divider class="w-115! max-w-[90%]! bg-[#1B4960]! my-1! " />
                    <Box class="flex flex-wrap" gap={2}>
                      <Box position="relative">
                        <TextField
                          class="w-[250px]!"
                          onKeyPress={async (e) => {
                            if (e.key === 'Enter') {
                              const input = e.currentTarget as HTMLInputElement;
                              if (input.value === '') {
                                setProspectMatching((prev) => ({
                                  ...prev,
                                  candidates: [],
                                  prospectMatches: [],
                                  prospectsSearched: false,
                                  tooManyMatches: false,
                                }));
                              }
                              await reviewPotentialMatches();
                            }
                          }}
                          controlStyle={activeCustomerLookupControlStyle()}
                          endIcon={
                            !isProspectFuzzyApiEnabled()
                              ? activeCustomerLookUpSearchIcon()
                              : undefined
                          }
                          label="Search Across the Company"
                          placeholder={
                            isProspectFuzzyApiEnabled()
                              ? 'Company name'
                              : 'Search Across the Company'
                          }
                          value={propspectMatching().prospectName}
                          onChange={(v: string) =>
                            setProspectMatching((prev) => ({
                              ...prev,
                              prospectName: v,
                              candidates: [],
                              prospectMatches: [],
                              prospectsSearched: false,
                              tooManyMatches: false,
                            }))
                          }
                          dataTestId={'search-across-the-company-textInput'}
                        />
                        <Show
                          when={
                            !isProspectFuzzyApiEnabled() &&
                            !propspectMatching().tooManyMatches &&
                            propspectMatching().prospectMatches.length > 0
                          }
                        >
                          <Stack class={cls.matchingCustomersContainer}>
                            <IconButton
                              class="absolute! right-2 top-2 p-0!"
                              onClick={() =>
                                setProspectMatching((prev) => ({
                                  ...prev,
                                  prospectMatches: [],
                                  prospectsSearched: false,
                                  tooManyMatches: false,
                                }))
                              }
                              dataTestId="close-prospect-matches-button"
                            >
                              <CloseCircleOutlineIcon class="text-[#B00020]!" />
                            </IconButton>
                            <Typography
                              variant="body1"
                              class={cls.matchingCustomersText}
                              component="strong"
                            >
                              Matching Customers
                            </Typography>
                            <For each={propspectMatching().prospectMatches}>
                              {(item) => (
                                <Stack class="p-2! pt-0!">
                                  <Typography
                                    variant="body1"
                                    component="strong"
                                    class={cls.matchingCustomersName}
                                  >
                                    {item.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    class={cls.matchingCustomersName}
                                  >
                                    <Typography
                                      variant="body2"
                                      component="strong"
                                      class={cls.matchingCustomersStatus}
                                    >
                                      Status :
                                    </Typography>{' '}
                                    {item.status}
                                  </Typography>
                                  <Show when={item.subStatus}>
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      class="text-gray-400! text-xs!"
                                    >
                                      {item.subStatus}
                                    </Typography>
                                  </Show>
                                </Stack>
                              )}
                            </For>
                          </Stack>
                        </Show>
                      </Box>
                      <Show when={isProspectFuzzyApiEnabled()}>
                        <TextField
                          class="w-[150px]!"
                          label="City"
                          placeholder="City (optional)"
                          value={propspectMatching().prospectCity}
                          onChange={(v: string) =>
                            setProspectMatching((prev) => ({
                              ...prev,
                              prospectCity: v,
                              candidates: [],
                            }))
                          }
                          dataTestId={'city-textInput'}
                        />
                      </Show>
                      <TextField
                        class="w-[100px]!"
                        label="State"
                        placeholder={
                          isProspectFuzzyApiEnabled()
                            ? 'State (optional)'
                            : 'State'
                        }
                        maxLength={2}
                        value={propspectMatching().prospectState}
                        onChange={(v: string) => {
                          const sanitized = sanitizeStateInput(v);
                          if (sanitized === propspectMatching().prospectState)
                            return;
                          setProspectMatching((prev) => ({
                            ...prev,
                            prospectState: sanitized,
                            candidates: [],
                            prospectMatches: [],
                            prospectsSearched: false,
                            tooManyMatches: false,
                          }));
                        }}
                        dataTestId={'state-textInput'}
                      />
                      <Button
                        label="Search"
                        variant="contained"
                        color="primary"
                        startIcon={<MagnifyIcon />}
                        title={
                          isProspectFuzzyApiEnabled()
                            ? 'Review potential matches'
                            : 'Search'
                        }
                        disabled={
                          (isProspectFuzzyApiEnabled()
                            ? propspectMatching().prospectName.trim().length < 3
                            : !propspectMatching().prospectName.trim()) ||
                          propspectMatching().isLoading
                        }
                        onClick={reviewPotentialMatches}
                        dataTestId={formatDataTestId('Search', 'button')}
                      />
                    </Box>
                  </Stack>
                </Grid>
                <Show when={activeView() === 'list'}>
                  <Grid item size={{ xs: 12, md: 12 }}>
                    <Box display="flex" alignItems="center" pt={2}>
                      <Typography variant="body2" class={cls.followUpText}>
                        Follow Ups:
                      </Typography>
                      <Box>
                        <TabsComponent
                          tabs={followUpTabs}
                          tabStyleClasses={cls.tabStyle}
                          tabContainerStyles={cls.tabContainer}
                          activeStyling={cls.activeTab}
                          activeTab={activeFollowUpTab()}
                          onTabClick={onListViewTabClick}
                        />
                        <div class="border-t border-gray-300 mb-2 ml-4" />
                      </Box>
                    </Box>
                  </Grid>
                </Show>
              </Grid>
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }} class="pt-0!">
              <DoNotSolicitCustomers title="Do Not Solicit" type="Prospect" />
            </Grid>
          </Grid>
        </Box>
        <Show when={activeView() === 'list'}>
          <Box class="flex-1  self-stretch overflow-y-auto min-h-62.5!">
            <ListView
              accountRep={filters().accountRep}
              prospectName={filters().prospectName}
              sortBy={sortBy}
              activeFollowUpTab={activeFollowUpTab}
              refetch={refetch}
              setRefetch={setRefetch}
            />
          </Box>
        </Show>
        <Show when={activeView() === 'table'}>
          <Box class={cls.tableViewGrid}>
            <AgGridSolid
              ref={gridRef}
              overlayNoRowsTemplate="No data available"
              overlayLoadingTemplate={atgLogo}
              columnDefs={columnDefs}
              rowHeight={30}
              getRowClass={(params) => {
                if (
                  params.node.rowIndex !== null &&
                  !(params.node.rowIndex % 2 === 0)
                ) {
                  return 'bg-[#026ea133]!';
                }
              }}
              rowClass={'cursor-pointer'}
              gridOptions={{
                defaultColDef: {
                  flex: 1,
                  filter: true,
                  wrapText: true,
                  autoHeight: true,
                  cellStyle: {
                    'line-height': '15px',
                  },
                },
                pagination: true,
                suppressPaginationPanel: false,
                maintainColumnOrder: true,
                paginationPageSizeSelector: false,
                sideBar: {
                  toolPanels: [columnsToolPanel, filtersToolPanel],
                },
                rowModelType: 'serverSide',
                getRowId: (params: { data: ProspectGridData }) =>
                  params.data.id as unknown as string,
                paginationPageSize: 1000,
                cacheBlockSize: 1000,
                onGridReady: onGridReady,
              }}
              onRowClicked={(e) => {
                const id = (e.data as { id: number }).id;
                if (routeProspectDetails(String(id))) {
                  navigate(`/Prospect/Details/${id}`);
                }
              }}
            />
          </Box>
        </Show>
        <ImportProspectModal refetch={refetch} setRefetch={setRefetch} />
        <BasicModal
          id={PROSPECT_DUPLICATE_CANDIDATES_MODAL_ID}
          title="Potential Customer or Prospect Matches"
          width="640px"
          footer={false}
          onClose={() => closeModal(PROSPECT_DUPLICATE_CANDIDATES_MODAL_ID)}
        >
          <Typography variant="body2" class="mb-3 text-slate-600">
            Only records available within your access scope are shown.
          </Typography>
          <Stack spacing={1}>
            <For each={propspectMatching().candidates}>
              {(candidate) => (
                <Box class="border border-slate-200 p-3">
                  <Box class="flex flex-wrap items-start justify-between gap-2">
                    <Box>
                      <Typography variant="body1" component="strong">
                        {candidate.companyName}
                      </Typography>
                      <Typography variant="body2" class="text-slate-600">
                        {candidate.recordType}{' '}
                        {candidate.status ? `| ${candidate.status}` : ''}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      class="font-semibold text-[#1B4960]"
                    >
                      {candidate.confidence}
                    </Typography>
                  </Box>
                  <Typography variant="body2" class="mt-2 text-slate-700">
                    {[candidate.location, candidate.maskedPhone]
                      .filter(Boolean)
                      .join(' | ')}
                  </Typography>
                  <Typography variant="body2" class="mt-1 text-slate-600">
                    {candidate.matchReasons.join(' and ')}
                  </Typography>
                  <Box display="flex" justifyContent="end" mt={2}>
                    <Button
                      label={`Open ${candidate.recordType.toLowerCase()}`}
                      variant="contained"
                      onClick={() => openCandidate(candidate)}
                      dataTestId={`open-${candidate.recordType.toLowerCase()}-${candidate.sourceId}-button`}
                    />
                  </Box>
                </Box>
              )}
            </For>
          </Stack>
        </BasicModal>
      </Stack>
    </>
  );
};

export default ProspectPage;
